import { useEffect } from "react"
import { observer } from "mobx-react-lite"
import { useNavigate, useParams } from "react-router-dom"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { formatPercentage } from "@/domains/library"
import { BurnPositionComposite } from "@/domains/thick-v1/components/burn-position"
import { CollectFeesComposite } from "@/domains/thick-v1/components/collect-fees"
import { DecreasePositionComposite } from "@/domains/thick-v1/components/decrease-position"
import { IncreasePositionComposite } from "@/domains/thick-v1/components/increase-position"
import { ManagePositionPriceHistoryComposite } from "@/domains/thick-v1/components/manage-position/manage-position-price-history-composite"
import { PositionHistoryView } from "@/domains/thick-v1/components/manage-position/position-history-view"
import { PositionStatsCardView } from "@/domains/thick-v1/components/manage-position/position-stats-card-view"
import { ManagePositionRangeBrushComposite } from "@/domains/thick-v1/components/range-brush/manage-position-range-brush-composite"
import { ManageSettingsPopoverComposite } from "@/domains/thick-v1/components/settings"
import { getFeeToTicksMapForChain, THICK_V1_BASE_ADDRESSES, THICK_V1_BASE_CHAIN_ID } from "@/domains/thick-v1/constants"

import type { ManagePositionHeaderViewProps } from "./manage-position-header-view"
import type { ManagePositionTabId } from "./manage-position-tabs-view"
import { ManagePositionLayoutView } from "./manage-position-layout-view"

/**
 * One-stop composite for every manage-position page.
 *
 * Each thin page (`increase.tsx`, `decrease.tsx`, `collect.tsx`, `close.tsx`,
 * `index.tsx`) renders this composite with its static `activeTab`. The
 * composite itself observes the collect-fees store because that store owns
 * the position reference we surface in the header — but any of the four
 * manage stores would resolve to the same position since they all share the
 * same `ManualClPositionRepository`.
 *
 * Tab navigation is the one concession to react-router inside the MobX
 * layer. A click on a tab routes to the matching sibling path rather than
 * toggling local state, so deep-linking a tab (e.g. `/manage/123/close`)
 * works without extra bookkeeping.
 *
 * The composite reads `useParams().id` and fans the active tokenId out to the
 * four manage stores through `ManualCl.setActiveManagePositionTokenId`, and
 * drives the per-position history store's active id so `Position_by_pk` is
 * only queried while a manage page is mounted.
 */
export const ManagePositionPageComposite = observer(({ activeTab }: { activeTab: ManagePositionTabId }) => {
  const scaleApp = useScaleApplication()
  const navigate = useNavigate()
  const params = useParams()
  const routeTokenId = typeof params.id === "string" ? params.id : undefined

  const increaseStore = scaleApp.ManualClV1.Increase
  const decreaseStore = scaleApp.ManualClV1.Decrease
  const collectStore = scaleApp.ManualClV1.Collect
  const burnStore = scaleApp.ManualClV1.Burn
  const positionHistoryStore = scaleApp.ManualClV1.PositionHistory
  const status = collectStore.resolvedStatus
  const manageStores = [increaseStore, decreaseStore, collectStore, burnStore] as const
  const routeNfpmPositionId = buildNfpmPositionIdFromRouteTokenId(routeTokenId)

  useEffect(() => {
    scaleApp.ManualClV1.setActiveManagePositionTokenId(routeTokenId)
    return () => {
      scaleApp.ManualClV1.setActiveManagePositionTokenId(undefined)
    }
  }, [scaleApp, routeTokenId])

  const resolvedPosition = status.kind === "found" ? status.position : undefined
  const positionId = resolvedPosition?.positionId
  const nfpmPositionId =
    routeNfpmPositionId ??
    (resolvedPosition?.nfpmTokenId !== undefined
      ? buildNfpmPositionId(resolvedPosition.nfpmTokenId.toString())
      : undefined)

  // Register the resolved position id with the history store on mount /
  // swap, and clear it on unmount. The store is a singleton across the
  // namespace, so failing to clear would leak rows into an unrelated
  // page render.
  useEffect(() => {
    if (!positionId && !nfpmPositionId) return
    positionHistoryStore.setActivePositionId(positionId, nfpmPositionId)
    return () => {
      positionHistoryStore.setActivePositionId(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, nfpmPositionId])

  // Kick the on-chain stats query the moment a manage page mounts so
  // `pendingFees0/1` land within an RPC round-trip instead of up to a full
  // 60 s polling interval. The orchestrator no-ops when the active
  // position already has stats, so tab switches on a cached position are
  // free.
  useEffect(() => {
    if (!positionId) return
    scaleApp.ManualClV1.PositionsOrchestrator.refreshOnChainStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId])

  // Point the shared tick-distribution store at this position's pool so
  // the forward-looking `currentRate` APR has an active-liquidity
  // denominator to compute against. The create-position flow uses the
  // same store — its own reaction takes over when the user navigates
  // away, so we don't need to clear on unmount.
  const activePool = status.kind === "found" ? status.position.pool : undefined
  const activePoolAddressForTickDist = activePool?.address.raw
  const feeToTicksMap = getFeeToTicksMapForChain(scaleApp.Config.chainId)
  const activePoolTickSpacing =
    activePool === undefined ? undefined : feeToTicksMap[activePool.feeTier as keyof typeof feeToTicksMap]
  useEffect(() => {
    if (!activePoolAddressForTickDist || activePoolTickSpacing === undefined) return
    scaleApp.ManualClV1.TickDistribution.setActivePool(activePoolAddressForTickDist, activePoolTickSpacing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePoolAddressForTickDist, activePoolTickSpacing])

  if (status.kind === "loading" || status.kind === "idle") {
    return <div className="rounded-lg border border-p400-light bg-p400 p-4 text-sm text-t300">Loading position...</div>
  }

  if (status.kind === "missing") {
    if (activeTab === "history" && routeNfpmPositionId) {
      return (
        <div className="flex flex-col gap-3">
          <div className="rounded-lg border border-p400-light bg-p400 p-4 text-sm text-t300">
            This NFT is not attached to an active range position. Showing its per-NFT indexer history when available.
          </div>
          <PositionHistoryView
            rows={positionHistoryStore.rows}
            isLoading={positionHistoryStore.isLoading}
            error={positionHistoryStore.error}
            token0Symbol={positionHistoryStore.token0Symbol ?? "Token 0"}
            token1Symbol={positionHistoryStore.token1Symbol ?? "Token 1"}
            onReload={() => positionHistoryStore.reload()}
          />
        </div>
      )
    }
    return (
      <div className="rounded-lg border border-p400-light bg-p400 p-4 text-sm text-t300">
        This position was not found or is not owned by the connected wallet.
      </div>
    )
  }

  const position = status.position
  const pool = position.pool
  const token0 = pool.token0
  const token1 = pool.token1
  const poolAddress = pool.address.raw as `0x${string}`
  const manageRouteTokenId = routeTokenId ?? position.tokenId.toString()
  const bestApr = position.bestAprDisplay

  const handleTabChange = (tab: ManagePositionTabId): void => {
    const base = `/liquidity/cl/v1/${poolAddress}/manage/${manageRouteTokenId}`
    if (tab === "overview") {
      navigate(base)
      return
    }
    navigate(`${base}/${tab}`)
  }

  const header: ManagePositionHeaderViewProps = {
    pair: {
      token0,
      token1,
      displayName: pool.displayName,
      feeLabel: formatPercentage(pool.feeTier / 1e6),
    },
    tokenId: position.nfpmTokenId?.toString() ?? "",
    nfpmTokenIdPending: position.nfpmTokenIdPending,
    displayRange: position.displayRange,
    displayMidPrice: pool.displayMidPrice,
    isInRange: position.isInRange,
    realisedAprFormatted: position.realisedApr.formattedPct,
    currentRate: {
      formattedPct: bestApr.label === "Accrued" ? position.currentRate.formattedPct : bestApr.formattedPct,
      formattedDailyUsd: bestApr.formattedDailyUsd || position.currentRate.formattedDailyUsd,
      inRange: bestApr.status !== "out-of-range",
      label: bestApr.label === "Accrued" ? "Current" : bestApr.label,
      status: bestApr.label === "Accrued" && position.currentRate.aprPct > 0 ? "ready" : bestApr.status,
    },
    timeInRangePct7d: position.timeInRangePct7d,
    timeInRangeSampleHours: position.timeInRangeSampleHours,
    onClaimFees: () => void collectStore.submit(),
    canCollectFees: collectStore.canSubmit,
    isCollectingFees: collectStore.isSubmitting,
  }

  const primary =
    activeTab === "decrease"
      ? decreaseStore
      : activeTab === "collect"
        ? collectStore
        : activeTab === "close"
          ? burnStore
          : increaseStore

  const overview = (
    <div className="flex flex-col gap-4">
      <PositionStatsCardView
        positionValueUsd={position.positionValueUsd}
        claimableFeesUsd={position.claimableFeesUsd}
        claimableFeesToken0={parsePendingFeeAmount(position.pendingFees0, token0)}
        claimableFeesToken1={parsePendingFeeAmount(position.pendingFees1, token1)}
        earnedFeesUsd={position.earnedFeesUsd}
        earnedFeesToken0={position.earnedFeesToken0}
        earnedFeesToken1={position.earnedFeesToken1}
        depositedToken0={position.totalDepositedToken0}
        depositedToken1={position.totalDepositedToken1}
        token0Symbol={token0.symbol}
        token1Symbol={token1.symbol}
        roiPct={position.roiPct}
        netPnlUsd={position.netPnlUsd}
      />
      <CollectFeesComposite store={collectStore} />
      <ManagePositionPriceHistoryComposite positionStore={position} poolOhlcStore={scaleApp.ManualClV1.PoolOhlc} />
      <ManagePositionRangeBrushComposite
        positionStore={position}
        tickDistributionStore={scaleApp.ManualClV1.TickDistribution}
      />
      <div className="text-sm text-t200">
        Use the tabs above to add, remove, collect fees, close this position, or view its event history.
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="neutral" size="sm" onClick={() => handleTabChange("increase")}>
          Add
        </Button>
        <Button variant="neutral" size="sm" onClick={() => handleTabChange("decrease")}>
          Remove
        </Button>
        <Button variant="neutral" size="sm" onClick={() => handleTabChange("collect")}>
          Fees
        </Button>
        <Button variant="neutral" size="sm" onClick={() => handleTabChange("history")}>
          History
        </Button>
      </div>
    </div>
  )

  return (
    <ManagePositionLayoutView
      header={header}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      trailingActions={<ManageSettingsPopoverComposite primary={primary} stores={manageStores} />}
      overview={overview}
      increase={<IncreasePositionComposite store={increaseStore} />}
      decrease={<DecreasePositionComposite store={decreaseStore} />}
      collect={<CollectFeesComposite store={collectStore} />}
      close={<BurnPositionComposite store={burnStore} />}
      history={
        <PositionHistoryView
          rows={positionHistoryStore.rows}
          isLoading={positionHistoryStore.isLoading}
          error={positionHistoryStore.error}
          token0Symbol={token0.symbol}
          token1Symbol={token1.symbol}
          onReload={() => positionHistoryStore.reload()}
        />
      }
    />
  )
})

function buildNfpmPositionIdFromRouteTokenId(tokenId: string | undefined): string | undefined {
  if (!tokenId || !/^\d+$/.test(tokenId)) return undefined
  return buildNfpmPositionId(tokenId)
}

function buildNfpmPositionId(tokenId: string): string {
  return `${THICK_V1_BASE_ADDRESSES.nfPositionManager.toLowerCase()}-${tokenId}-${THICK_V1_BASE_CHAIN_ID}`
}

function parsePendingFeeAmount(
  amount: bigint | undefined,
  token: { formatUnits: (value: bigint) => string },
): number | undefined {
  if (amount === undefined) return undefined
  const parsed = Number(token.formatUnits(amount))
  return Number.isFinite(parsed) ? parsed : undefined
}
